import React from 'react';
import { useTranslation } from "react-i18next";
import HeroSection from '../../components/sections/HeroSection';
import BigTinyIntroSection from '../../components/sections/BigTinyIntroSection';
import OnePlusTwoGallery from '../../components/sections/OnePlusTwoGallery';
import FaqAccordionsSection from '../../components/sections/FaqAccordionsSection';
import FactsAndStatsSection from '../../components/sections/FactsAndStatsSection';
import PracticalInfoSection from '../../components/sections/PracticalInfoSection';
import ArticleWithModal from '../../components/sections/ArticleWithModal';
import VisaServicesPromotion from '../../components/sections/VisaServicesPromotion';
import FooterSection from '../../components/sections/FooterSection';
import DividersInfoSection from '../../components/sections/DividersInfoSection';
import ParallaxCallSection from '../../components/sections/ParallaxCallSection';

function SemeyCityPage(props) {

    var { t } = useTranslation();

    var faqItems = [
        { question: "What documents are required?", answer: "Passport valid for at least 6 months, visa application form, passport photos, proof of accommodation, and travel insurance are standard requirements." },
        { question: "How long does processing take?", answer: "Standard processing usually takes up to 5 working days, while express processing can be completed in 48 hours depending on consular workload." },
        { question: "Can business delegations apply?", answer: "Yes, corporate visa packages are available for delegations, including bulk submissions, account-based invoicing, and consolidated document delivery." },
        { question: "Is visa on arrival available?", answer: "Visa on arrival is limited to specific cases. Pre-application ensures guaranteed entry and avoids administrative delays at the border." }
    ];

    return (
        <>
            <HeroSection
                isExpectingAnimations={true}
                title="Semey: Your Gateway to Education and Industry in Eastern Kazakhstan"
                paragraph="Discover Semey, a city rich in history and culture, now a growing hub for education and industry. Jai makes it easy for students and workers to get their visas and start a new chapter in this welcoming city."
                callToAction="Begin Your Semey Journey with Jai!"
                backgroundImageUrl="https://welcome.kz/ru/assets/images/products/0_gallery/locations/altay/semey2.jpeg"
            />
            
            <div className='uk-container'>
                <BigTinyIntroSection
                    centered={false}
                    Big="Semey offers a unique blend of quiet academic life and growing industrial opportunities. For students seeking quality education without the chaos of a megacity, and for workers looking for stable jobs in construction and services, Semey is an ideal choice. Jai is your partner in navigating the visa process, ensuring a smooth transition to your new life."
                    Tiny="Imagine studying at a historic university or building a career in a city that values growth and community. With Jai's expert visa assistance, you can confidently pursue your educational and professional goals in one of Eastern Kazakhstan's most important centers."
                />

                <section className="uk-grid-large uk-child-width-1-2@s uk-flex-middle" data-uk-grid>
                    <div>
                        <h1 className="uk-heading-medium uk-text-bold uk-margin-remove">
                            Semey — A City of Heritage and Progress
                        </h1>

                        <p className="uk-text-lead uk-margin-small-top">
                            Located on the banks of the Irtysh River, Semey is a major city in eastern Kazakhstan with a rich intellectual and cultural history. It is evolving into a key industrial and logistical hub for the region, creating new opportunities for workers and students.
                        </p>

                        <p>
                            Historically known as Semipalatinsk, the city was a center for literature and education, home to figures like the great Kazakh poet Abai Kunanbaev. Today, it honors that legacy through its universities and museums while embracing modern development. The city's economy is diversifying from agriculture to include food processing, construction, and transport services.
                        </p>

                        <p>
                            For those seeking work, Semey's growth translates into a steady demand for skilled labor in construction projects and jobs in the expanding service industry. For students, the city offers a calm and focused environment for learning. Jai is here to help you access these opportunities by making your visa application process straightforward and successful.
                        </p>

                        <VisaServicesPromotion />
                    </div>

                    <OnePlusTwoGallery
                        BigImageLink="https://hisky.kz/wp-content/uploads/2020/06/5.jpg"
                        BigImageDescription="Irtysh River in Semey"
                        SmallImage1Link="https://rutraveller.ru/icache/place/1/260/043/126043_608x608.jpg"
                        SmallImage1Description="Abai Kunanbaev Museum"
                        SmallImage2Link="https://krisha-photos.kcdn.online/06/7cd3e488b4048286f4e01032ac869322702440/photo-750x470.jpg"
                        SmallImage2Description="Semey city architecture"
                    />

                </section>
            </div>

            <ParallaxCallSection
                URL="https://i.ytimg.com/vi/AYANyrif1JU/maxresdefault.jpg"
                headline="Build Your Future in Semey"
                callToAction="Let Jai handle your visa paperwork so you can focus on the opportunities waiting for you in Kazakhstan's historic eastern city."
            />

            <div className='uk-container'>
                <section className="uk-grid-large uk-child-width-1-3@m uk-margin-large-top" data-uk-grid>

                    <ArticleWithModal
                        Image="https://static.tengrinews.kz/userdata/news/2023/news_519613/thumb_m/photo_454477.jpeg"
                        ImageDescription="Construction work in Semey"
                        ArticleTitle="Work in a Growing Economy"
                        Paragraph1="Semey is undergoing significant urban renewal and industrial expansion, creating a strong and consistent demand for manual laborers and skilled tradespeople. New residential buildings, commercial centers, and infrastructure projects are constantly underway."
                        Paragraph2="There are abundant opportunities for construction workers, welders, electricians, and machine operators. The city's growing logistics and food processing industries also require a steady workforce for factory operations, warehousing, and transportation."
                        Paragraph3="Jai connects foreign workers with verified employers in Semey, ensuring you receive a formal job offer and a secure work visa. We help you find stable employment where your skills are valued, allowing you to build a prosperous future for yourself and your family in a welcoming community."
                        modalName="modal-work-semey"
                    />

                    <ArticleWithModal
                        Image="https://smu.edu.kz/wp-content/uploads/2019/09/img_3489.jpg"
                        ImageDescription="University building in Semey"
                        ArticleTitle="Affordable, Quality Education"
                        Paragraph1="Semey is home to several reputable universities, including the Alikhan Bokeikhan University and Shakarim State University, known for their strong programs in medicine, agriculture, and humanities. It offers a high-quality education at a more affordable cost of living compared to the capital."
                        Paragraph2="The city provides a peaceful and focused atmosphere for students, away from the distractions of larger metropolitan areas. Its rich cultural history and numerous libraries and museums create an intellectually stimulating environment perfect for academic pursuits."
                        Paragraph3="Our comprehensive student visa package simplifies your path to studying in Semey. Jai assists with everything from university admission to visa processing and accommodation, ensuring you can concentrate on your education from day one. Start your academic journey in a city that values knowledge and tradition."
                        modalName="modal-education-semey"
                    />

                    <PracticalInfoSection
                        sectionTitle="Practical Information"
                        currencyInfo="Local currency: Kazakhstani tenge (KZT)."
                        airportInfo="Primary airport: Semey Airport (PLX), with domestic flights and connections to major hubs."
                        climateInfo="Climate: Dry continental; warm summers and very cold winters with moderate snowfall."
                        businessHoursInfo="Business hours: Standard office hours are 09:00–18:00. Retail and service hours are generally longer."
                    />
                </section>
            </div>

            {/* Quick Facts / Statistics */}
            <FactsAndStatsSection
                sectionTitle="Quick Facts"
                stat1="90%"
                fact1="Visa approval rate"
                stat2="635+"
                fact2="Clients served"
                stat3="14"
                fact3="Days processing time"
                stat4="1718"
                fact4="Year city founded"
            />

            <div className="uk-section uk-container">
                <div className="uk-text-center">
                    <h2 className="uk-h2 uk-text-bold">Opportunities in a Developing Hub</h2>
                    <p className="uk-text-lead">
                        Semey's developing economy offers a stable job market for both skilled professionals and manual laborers, driven by construction, services, and light industry.
                    </p>
                </div>

                <div className="uk-child-width-1-3@m uk-grid-match uk-margin-medium-top" data-uk-grid>
                    <div>
                        <div className="uk-card uk-card-body uk-card-hover uk-card-default">
                            <div className="uk-card-badge uk-label">High Demand</div>
                            <h3 className="uk-card-title"><span data-uk-icon="icon: home; ratio: 1.2" className="uk-margin-small-right"></span>Construction & General Labour</h3>
                            <p>With ongoing urban development, there is a high demand for construction workers, foremen, and general laborers for new housing and commercial projects. This sector offers steady, long-term employment.</p>
                        </div>
                    </div>
                    <div>
                        <div className="uk-card uk-card-body uk-card-hover uk-card-default">
                            <h3 className="uk-card-title"><span data-uk-icon="icon: cog; ratio: 1.2" className="uk-margin-small-right"></span>Light Industry & Food Processing</h3>
                            <p>The city's industrial parks and food processing plants regularly seek factory workers, machine operators, and quality control staff. These roles provide stable jobs within a growing sector of the local economy.</p>
                        </div>
                    </div>
                    <div>
                        <div className="uk-card uk-card-body uk-card-hover uk-card-default">
                            <h3 className="uk-card-title"><span data-uk-icon="icon: world; ratio: 1.2" className="uk-margin-small-right"></span>Education & Healthcare</h3>
                            <p>As a regional educational and medical center, Semey has opportunities for teachers, university lecturers (especially with English proficiency), and medical professionals to work in its established institutions.</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* FAQ Accordion */}
            <FaqAccordionsSection
                items={faqItems}
            />

            <div className='uk-container'>
                <BigTinyIntroSection
                    centered={true}
                    Big="Whether you are coming to study, work, or build a new life, Semey offers a unique combination of peaceful living and tangible opportunity. It's a city where history informs the future and community matters."
                    Tiny="With Jai, your path to Kazakhstan is clear and secure. We handle the complexities of the visa process so you can confidently take the next step in your journey. Contact us today to learn how we can help you make Semey your new home."
                />

                {/* Infosection with shitloads of dividers */}
                <DividersInfoSection />
            </div>

            <FooterSection />
        </>
    );
}

export default SemeyCityPage;